import React, { PropsWithChildren, useCallback, useMemo } from 'react';
import { CategorySelectionType, setOpenedCategory } from './categoryTreeSlice';
import { PaddingSpan } from './PaddingSpan';
import { TreeLeafTitle, TreeLeafWrapper } from './categoryTreeStyledComponents';
import { useAppDispatch } from '../../../store';
import { OpenIcon, Row } from '@ydistri/ds';
import { styled } from 'styled-components';
import { CategoryTreePresentationMode } from './CategoryTreeTypes';
import { EnableCategoryField } from './EnableCategoryField';
import ExceptionIcon from './ExceptionIcon';
import useIsConfigurationEditable from '../../../hooks/useIsConfigurationEditable';

interface ControlsContainerProps {
  $visible?: boolean;
  $displayOnHover?: boolean;
}

const ControlsContainer = styled(Row)<ControlsContainerProps>`
  margin-left: auto;
  justify-content: flex-end;
  flex-wrap: nowrap;

  ${p =>
    !p.$visible &&
    `
  display: none;

  ${
    p.$displayOnHover &&
    `
      ${TreeLeafWrapper}:hover & {
        display: flex;
      }
   `
  }
  `}
`;

interface CategoryControlsContainerProps extends PropsWithChildren {
  $visible?: boolean;
  minWidth?: string;
  $displayOnHover?: boolean;
}

const CategoryControlsContainer: React.FC<CategoryControlsContainerProps> = ({
  $visible = false,
  minWidth,
  $displayOnHover = true,
  children,
}) => {
  return (
    <Row
      data-testid="CategoryControlsContainer"
      $minWidth={minWidth}
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onMouseDown={event => {
        event.stopPropagation();
      }}
    >
      <ControlsContainer $visible={$visible} $displayOnHover={$displayOnHover}>
        {children}
      </ControlsContainer>
    </Row>
  );
};

interface TreeLeafProps {
  slug: string;
  categoryId: number;
  title: string;
  selectionType: CategorySelectionType;
  opened: boolean;
  hasException: boolean;
  subcategoryHasException: boolean;
  level: number;
  lastLevel: boolean;
  onCategoryChange?: (event: React.MouseEvent, categorySlug: string) => void;
  search: string;
  disabled?: boolean;
  /* true if the disabled parameter is governed by its own value or by the parent */
  ownValue?: boolean;
  presentationMode?: CategoryTreePresentationMode;
}

const TreeLeaf: React.FC<TreeLeafProps> = ({
  slug,
  categoryId,
  title,
  selectionType,
  opened,
  hasException,
  subcategoryHasException,
  level,
  lastLevel,
  onCategoryChange,
  search,
  disabled = false,
  ownValue = false,
  presentationMode = CategoryTreePresentationMode.CONFIGURATION,
}) => {
  const dispatch = useAppDispatch();
  const isConfigurationEditable = useIsConfigurationEditable();
  const canBeOpened = !selectionType || selectionType === 'exact' || !opened;

  const onOpenHandler = useCallback(
    (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      if (canBeOpened) {
        dispatch(setOpenedCategory({ categorySlug: slug, opened: !opened }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- slug is not a dependency (but why?)
    [opened, canBeOpened],
  );

  const clickHandler = useCallback(
    (event: React.MouseEvent) => {
      onCategoryChange?.(event, slug);
    },
    [onCategoryChange, slug],
  );

  const expandIcon = useMemo(() => {
    if (level > 0) {
      if (!lastLevel) {
        return <OpenIcon $isOpened={opened} $canBeOpened={canBeOpened} onClick={onOpenHandler} />;
      } else {
        return <PaddingSpan width="2rem" />;
      }
    }
  }, [canBeOpened, lastLevel, level, onOpenHandler, opened]);

  const showExceptions =
    presentationMode === CategoryTreePresentationMode.CONFIGURATION ||
    presentationMode === CategoryTreePresentationMode.CALCULATION;
  const showActiveEnableButtons =
    isConfigurationEditable && presentationMode === CategoryTreePresentationMode.CONFIGURATION;

  return (
    <TreeLeafWrapper
      $level={level}
      $selectionType={selectionType}
      $isOpened={opened}
      to={`../${slug}${search}`}
      relative="path"
      onClick={clickHandler}
      $disabled={disabled}
      $presentationMode={presentationMode}
      data-testid={`TreeLeafWrapper-${slug}`}
    >
      {slug
        .split('-')
        .map(
          (categoryId, index) =>
            index > 1 && <PaddingSpan key={categoryId} data-testid={`PaddingSpan-${categoryId}`} />,
        )}
      {expandIcon}
      <Row $flexWrap="nowrap" $flexGrow={1}>
        <TreeLeafTitle
          data-testid="TreeLeafTitle"
          $disabled={disabled}
          $presentationMode={presentationMode}
        >
          {title}
        </TreeLeafTitle>
        {level > 0 && showExceptions && (
          <ExceptionIcon
            hasException={hasException}
            subcategoryHasException={subcategoryHasException}
            categoryId={categoryId}
          />
        )}

        <CategoryControlsContainer
          $visible={ownValue && showExceptions}
          $displayOnHover={showActiveEnableButtons}
          minWidth="40px"
        >
          <EnableCategoryField categoryId={categoryId} disabled={!showActiveEnableButtons} />
        </CategoryControlsContainer>
      </Row>
    </TreeLeafWrapper>
  );
};

export default React.memo(TreeLeaf);
